import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio } from 'antd';
import SortableList from '@components/SortableList';
import { IDevice } from '@pages/Assets/Inventory/types';
import CustomCard from '@components/CustomCard';
import { IDeviceActionRequestPayload, IOperatorsPriority } from '../types';

interface IPriorities {
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  actionKey: number;
  devicesProps: IDevice[] | undefined;
  uploadedDevices: any;
  dataOperators?: IOperatorsPriority[];
}

const useActionPriorities = ({
  actionKey,
  devicesProps,
  uploadedDevices,
  handleDeviceActions,
  dataOperators,
}: IPriorities) => {
  const defatulOperator = useMemo(() => {
    return dataOperators?.map(item => {
      return {
        title: item.operator,
      };
    });
  }, [dataOperators]);

  const [selectedPriorityArr, setSelectedPriorityArr] = useState<
    { title: string }[] | undefined
  >(defatulOperator);
  const [resetList, setResetList] = useState<number>(1);

  const { t: translate } = useTranslation(['device-actions-modal']);

  const handleChangeListOperators = useCallback(
    (value: { title: string }[]) => {
      setSelectedPriorityArr(value);
    },
    [],
  );

  const contentSteps = useMemo(() => {
    return [
      {
        title: translate('steps.priority'),
        content: (
          <Radio.Group
            onChange={e => {
              setResetList(e.target.value);
            }}
            value={resetList}
          >
            <Radio value={1} style={{ width: '100%' }}>
              {translate('select-operators')}
            </Radio>
            <CustomCard style={{ margin: '16px 0' }}>
              <SortableList
                disabled={resetList !== 1}
                items={defatulOperator}
                onChange={handleChangeListOperators}
              />
            </CustomCard>
            <Radio value={2}>
              {devicesProps && devicesProps.length > 0
                ? translate('reset-list_other')
                : translate('reset-list_one')}
            </Radio>
          </Radio.Group>
        ),
      },
    ];
  }, [
    handleChangeListOperators,
    resetList,
    translate,
    defatulOperator,
    devicesProps,
  ]);

  const reinitState = () => {
    setSelectedPriorityArr(
      dataOperators?.map(item => {
        return {
          title: item.operator,
        };
      }),
    );
    setResetList(1);
  };

  const onSubmit = useCallback(async () => {
    const devicesToSend =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(device => ({
            msisdn: device.msisdn,
            iccid: device.iccid,
          }))
        : uploadedDevices;

    const payload: IDeviceActionRequestPayload = {};
    payload.action = actionKey;
    payload.devices = devicesToSend || [];

    if (resetList === 1) {
      payload.operators = selectedPriorityArr?.map(({ title }) => title);
    } else {
      payload.operators = [];
    }
    return handleDeviceActions(payload);
  }, [
    actionKey,
    handleDeviceActions,
    devicesProps,
    resetList,
    selectedPriorityArr,
    uploadedDevices,
  ]);

  const prioritiesData = {
    contentSteps,
    onSubmit,
    reinitState,
  };

  return { prioritiesData };
};

export default useActionPriorities;
